import { apiClient } from "./client";

export interface MessageConversation {
    id: number;
    type: string;
    title: string;
    contextType: string | null;
    contextId: string | null;
    peerUserId: number | null;
    /** Само за директен чат; null при context/групови. */
    isOnline: boolean | null;
    /** Последна активност (ISO); null ако няма данни. */
    lastSeenAt: string | null;
    lastMessageBody: string | null;
    lastMessageAt: string | null;
    unreadCount: number;
    updatedAt: string;
}

export interface MessageRow {
    id: number;
    conversationId: number;
    senderUserId: number;
    senderName: string;
    body: string;
    attachments: MessageAttachment[];
    createdAt: string;
}

export interface MessageAttachment {
    id: number;
    originalFilename: string;
    mimeType: string;
    fileSize: number;
}

export interface MessageUnreadPreview {
    messageId: number;
    conversationId: number;
    senderName: string;
    body: string;
    createdAt: string;
    conversationTitle: string;
    conversationType: string;
}

export interface MessagesUnreadCountResponse {
    unreadCount: number;
    preview: MessageUnreadPreview[];
    lastUpdatedAt: string;
}

export interface MessagesInboxResponse {
    conversations: MessageConversation[];
    unreadCount: number;
    lastUpdatedAt: string;
}

export interface MessagesThreadResponse {
    conversationId: number;
    messages: MessageRow[];
    unreadCount: number;
    lastUpdatedAt: string;
}

export async function fetchMessagesInbox() {
    const { data } = await apiClient.get<MessagesInboxResponse>("/messages/inbox");
    return data;
}

export async function fetchMessagesUnreadCount() {
    const { data } = await apiClient.get<MessagesUnreadCountResponse>("/messages/unread-count");
    return data;
}

export async function fetchMessageColleagues() {
    const { data } = await apiClient.get<{
        users: Array<{ id: number; name: string; email: string; isOnline: boolean; lastSeenAt: string | null }>;
    }>("/messages/colleagues");
    return data;
}

export async function sendMessagesPresenceHeartbeat() {
    const { data } = await apiClient.post<{ ok: boolean; lastUpdatedAt: string }>("/messages/presence");
    return data;
}

export async function openDirectConversation(peerUserId: number) {
    const { data } = await apiClient.post<MessagesThreadResponse>("/messages/direct", {
        peer_user_id: peerUserId,
    });
    return data;
}

export async function openContextConversation(
    contextType: "client" | "order" | "campaign",
    contextId: string,
    title?: string,
) {
    const { data } = await apiClient.post<MessagesThreadResponse>("/messages/context", {
        context_type: contextType,
        context_id: contextId,
        title: title ?? "",
    });
    return data;
}

export async function fetchConversationThread(conversationId: number) {
    const { data } = await apiClient.get<MessagesThreadResponse>(`/messages/conversations/${conversationId}`);
    return data;
}

export async function sendConversationMessage(conversationId: number, body: string, files: File[] = []) {
    const formData = new FormData();
    formData.append("body", body);

    files.forEach((file) => {
        formData.append("files[]", file);
    });

    const { data } = await apiClient.post<{
        message: MessageRow;
        unreadCount: number;
        lastUpdatedAt: string;
    }>(`/messages/conversations/${conversationId}/messages`, formData);

    return data;
}

export async function downloadMessageAttachment(attachmentId: number, filename: string) {
    const response = await apiClient.get<Blob>(`/messages/attachments/${attachmentId}`, {
        responseType: "blob",
    });

    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export async function markConversationRead(conversationId: number) {
    const { data } = await apiClient.post<{ unreadCount: number; lastUpdatedAt: string }>(
        `/messages/conversations/${conversationId}/read`,
    );
    return data;
}
